import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useBeratungSession } from "@/hooks/useBeratungSessions";
import { useBeratungSeiten, BeratungSeite } from "@/hooks/useBeratungSeiten";
import { KundenPanel } from "./KundenPanel";
import { supabase } from "@/integrations/supabase/client";
import { fromTable } from "@/integrations/supabase/db";
import type { DrawingContent } from "./DrawingCanvas";

interface BroadcastMessage {
  type: 'beratung-sync' | 'beratung-hover-sync';
  drawingContent?: DrawingContent;
  isDrawingShared?: boolean;
  isDrawingCollaborative?: boolean;
  formValues?: Record<string, any>;
  currentSeiteId?: string;
  /** Full seite data with tile overrides applied */
  currentSeiteData?: BeratungSeite;
  /** Hovered tile ID for synchronization */
  hoveredTileId?: string | null;
}

// Parse drawing_content from DB which may include shared/collaborative flags
function parseDrawingContentWithFlags(raw: any): {
  drawingContent: DrawingContent;
  shared: boolean;
  collaborative: boolean;
} {
  if (!raw) {
    return { drawingContent: { drawing: '', text: '' }, shared: false, collaborative: false };
  }
  
  if (typeof raw === 'string') {
    try {
      raw = JSON.parse(raw);
    } catch {
      return { drawingContent: { drawing: '', text: '' }, shared: false, collaborative: false };
    }
  }
  
  return {
    drawingContent: {
      drawing: raw.drawing || '',
      text: raw.text || '',
    },
    shared: raw.shared === true,
    collaborative: raw.collaborative === true,
  };
}

export function KundenViewStandalone() {
  const { sessionId } = useParams<{ sessionId: string }>();
  
  // Local state synced via Supabase Realtime or BroadcastChannel
  const [drawingContent, setDrawingContent] = useState<DrawingContent>({ drawing: '', text: '' });
  const [isDrawingShared, setIsDrawingShared] = useState(false);
  const [isDrawingCollaborative, setIsDrawingCollaborative] = useState(false);
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [tileOverrides, setTileOverrides] = useState<Record<string, any[]>>({});
  const [currentSeiteId, setCurrentSeiteId] = useState<string | undefined>();
  const [hoveredTileId, setHoveredTileId] = useState<string | null>(null);
  
  // Track if we received broadcast data (to prioritize it over DB for same-browser)
  const hasBroadcastDataRef = useRef(false);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  
  const { data: session } = useBeratungSession({
    sessionId,
    enabled: !!sessionId,
  });

  const { data: allSeiten = [] } = useBeratungSeiten({
    modulIds: session?.ausgewaehlte_module || [],
    enabled: !!session?.ausgewaehlte_module?.length,
  });

  // Hydrate from DB session on initial load
  useEffect(() => {
    if (!session) return;
    
    // Only hydrate if we haven't received broadcast data yet
    if (!hasBroadcastDataRef.current) {
      // Drawing content
      if (session.drawing_content) {
        const parsed = parseDrawingContentWithFlags(session.drawing_content);
        setDrawingContent(parsed.drawingContent);
        setIsDrawingShared(parsed.shared);
        setIsDrawingCollaborative(parsed.collaborative);
      }
      
      // Form values
      if (session.form_values && Object.keys(session.form_values).length > 0) {
        setFormValues(session.form_values);
      }
      
      // Tile overrides
      if (session.tile_overrides && Object.keys(session.tile_overrides).length > 0) {
        setTileOverrides(session.tile_overrides);
      }
      
      // Current page
      if (session.aktuelle_seite_id) {
        setCurrentSeiteId(session.aktuelle_seite_id);
      }
    }
  }, [session]);

  // Supabase Realtime for cross-device synchronization
  useEffect(() => {
    if (!sessionId) return;
    
    const channel = supabase
      .channel(`customer-session-${sessionId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'crm',
          table: 'beratung_sessions',
          filter: `id=eq.${sessionId}`,
        },
        (payload) => {
          const newData = payload.new as any;
          
          // Sync tile_overrides
          if (newData.tile_overrides) {
            setTileOverrides(newData.tile_overrides);
          }
          
          // Sync form_values
          if (newData.form_values) {
            setFormValues(newData.form_values);
          }
          
          // Sync current page
          if (newData.aktuelle_seite_id) {
            setCurrentSeiteId(newData.aktuelle_seite_id);
          }
          
          // Sync drawing content
          if (newData.drawing_content) {
            const parsed = parseDrawingContentWithFlags(newData.drawing_content);
            setDrawingContent(parsed.drawingContent);
            setIsDrawingShared(parsed.shared);
            setIsDrawingCollaborative(parsed.collaborative);
          }
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [sessionId]);

  // Handle tile selection in customer view - persist to DB
  const handleTileSelect = useCallback((tileId: string) => {
    setFormValues(prev => {
      const currentPriorities: string[] = prev['selected_priorities'] || [];
      const updated = currentPriorities.includes(tileId)
        ? currentPriorities.filter(id => id !== tileId)
        : [...currentPriorities, tileId];
      
      const newFormValues = { ...prev, selected_priorities: updated };
      
      // Persist to DB for cross-device sync
      if (sessionId) {
        fromTable('beratung_sessions')
          .update({ form_values: newFormValues })
          .eq('id', sessionId)
          .then(() => {});
      }
      
      // Also send via BroadcastChannel for same-browser performance
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.postMessage({
          type: 'beratung-sync-reply',
          formValues: newFormValues,
        });
      }
      
      return newFormValues;
    });
  }, [sessionId]);

  // Handle tile hover - send to main window (same browser only via broadcast)
  const handleTileHover = useCallback((tileId: string | null) => {
    setHoveredTileId(tileId);
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'beratung-hover-sync',
        hoveredTileId: tileId,
      });
    }
  }, []);

  // Handle drawing changes from customer (when collaborative)
  const handleDrawingChange = useCallback((newContent: DrawingContent) => {
    setDrawingContent(newContent);
    
    // Persist to DB for cross-device sync
    if (sessionId) {
      const drawingPayload = {
        ...newContent,
        shared: isDrawingShared,
        collaborative: isDrawingCollaborative,
      };
      fromTable('beratung_sessions')
        .update({ drawing_content: drawingPayload })
        .eq('id', sessionId)
        .then(() => {});
    }
    
    // Also send via BroadcastChannel for same-browser performance
    if (broadcastChannelRef.current) {
      broadcastChannelRef.current.postMessage({
        type: 'beratung-sync-reply',
        drawingContent: newContent,
      });
    }
  }, [sessionId, isDrawingShared, isDrawingCollaborative]);

  // BroadcastChannel for same-browser fast sync (fallback/enhancement)
  useEffect(() => {
    if (!sessionId) return;
    
    const channel = new BroadcastChannel(`beratung-sync-${sessionId}`);
    broadcastChannelRef.current = channel;
    
    // Request initial state from main window
    channel.postMessage({ type: 'beratung-sync-ready' });
    
    channel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
      if (event.data.type === 'beratung-sync') {
        hasBroadcastDataRef.current = true;
        if (event.data.drawingContent) {
          setDrawingContent(event.data.drawingContent);
        }
        if (event.data.isDrawingShared !== undefined) {
          setIsDrawingShared(event.data.isDrawingShared);
        }
        if (event.data.isDrawingCollaborative !== undefined) {
          setIsDrawingCollaborative(event.data.isDrawingCollaborative);
        }
        if (event.data.formValues) {
          setFormValues(event.data.formValues);
        }
        if (event.data.currentSeiteId) {
          setCurrentSeiteId(event.data.currentSeiteId);
        }
        // Extract tile overrides from currentSeiteData if provided
        if (event.data.currentSeiteData?.kunden_layout?.tiles) {
          const seiteId = event.data.currentSeiteData.id;
          setTileOverrides(prev => ({
            ...prev,
            [seiteId]: event.data.currentSeiteData!.kunden_layout.tiles,
          }));
        }
      }
      // Handle hover synchronization from main window
      if (event.data.type === 'beratung-hover-sync') {
        setHoveredTileId(event.data.hoveredTileId ?? null);
      }
    };
    
    return () => {
      channel.close();
      broadcastChannelRef.current = null;
    };
  }, [sessionId]);

  // Compute current seite with tile overrides applied
  const currentSeite = useMemo(() => {
    const seiteId = currentSeiteId || session?.aktuelle_seite_id;
    const baseSeite = seiteId 
      ? allSeiten.find(s => s.id === seiteId) 
      : allSeiten[0];
    
    if (!baseSeite) return null;
    
    // Apply tile overrides if they exist for this page
    const overrides = tileOverrides[baseSeite.id];
    if (overrides && overrides.length > 0) {
      return {
        ...baseSeite,
        kunden_layout: {
          ...baseSeite.kunden_layout,
          tiles: overrides,
        },
      };
    }
    
    return baseSeite;
  }, [currentSeiteId, session?.aktuelle_seite_id, allSeiten, tileOverrides]);

  // Fullscreen view - no header needed for standalone customer tab
  return (
    <div className="h-screen w-screen bg-muted/30 flex flex-col overflow-y-auto">
      <KundenPanel
        seite={currentSeite} 
        formValues={formValues}
        drawingContent={drawingContent}
        onDrawingChange={isDrawingCollaborative ? handleDrawingChange : undefined}
        isDrawingShared={isDrawingShared}
        isDrawingCollaborative={isDrawingCollaborative}
        drawingPlacement="inline"
        fullscreen
        showHeader={false}
        interactiveTiles
        onTileSelect={handleTileSelect}
        hoveredTileId={hoveredTileId}
        onTileHover={handleTileHover}
      />
    </div>
  );
}
